import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { usePostHog } from 'posthog-js/react';
// core components
import GridContainer from '@/components/Grid/GridContainer';
import GridItem from '@/components/Grid/GridItem';
// import Table from "components/Table/Table";
import Card from '@/components/Card/Card';
import CardBody from '@/components/Card/CardBody';
import CardHeader from '@/components/Card/CardHeader';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import loader from '../../assets/img/loader.svg';
// components for table.
import TablePaginationActions from '@/views/Managefile/TablePaginationActions/TablePaginationActions';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableFooter from '@mui/material/TableFooter';
import TableHead from '@mui/material/TableHead';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import { OrganizationsApi } from '@/api/clients/organizations/organizations.api';
import { useSearchOrganizations } from '@/api/hooks/organizations/useSearchOrganizations';
import { FACILITY_PORTAL_URL } from '@/config';
import Chip from '@mui/material/Chip';

const MAX_SEARCH_LENGTH = 255;
const CENTERED_LOADER_STYLE = {
  top: '50%',
  left: '50%',
  marginLeft: 0,
  transform: 'translate(-50%, -50%)',
};

export default function Organizations() {
  const { t } = useTranslation();
  const posthog = usePostHog();
  const [searchTerm, setSearchTerm] = useState('');
  const {
    organizations,
    searchedTerm,
    hasError,
    errorMessage,
    isLoading,
  } = useSearchOrganizations(searchTerm);
  const [pageSelection, setPageSelection] = useState({
    page: 0,
    organizations,
  });
  const page =
    pageSelection.organizations === organizations ? pageSelection.page : 0;
  const [rowsPerPage, setRowsPerPage] = useState(25);

  const selectPage = (newPage) => {
    setPageSelection({ page: newPage, organizations });
  };

  const handleChangePage = (event, newPage) => {
    selectPage(newPage);
  };

  const handleRowsPerPage = (event) => {
    let rows = parseInt(event.target.value);
    setRowsPerPage(rows);
    selectPage(0);
  };

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
  };

  const getEmptyTableMessage = () => {
    if (hasError) {
      return t('organizations.loadError');
    }
    return searchedTerm
      ? t('organizations.noSearchResults')
      : t('organizations.noData');
  };

  const handleLoginToOrganization = async (organizationId) => {
    try {
      await OrganizationsApi.setActiveOrganization(organizationId);

      const selectedOrganization = organizations.find(
        (org) => org.id === organizationId,
      );

      if (selectedOrganization) {
        posthog.group('organization', selectedOrganization.id, {
          name: selectedOrganization.name,
        });
      }

      const userId = localStorage.getItem('userId');
      const userEmail = localStorage.getItem('loginEmail');

      if (userId && selectedOrganization) {
        posthog.identify(JSON.parse(userId), {
          name: userEmail,
          email: userEmail,
          organization: selectedOrganization.name,
        });
      }

      posthog.capture('admin login to organization', {
        organizationId: organizationId,
        organizationName: selectedOrganization?.name,
      });

      const organizationFeatures = await OrganizationsApi.getOrganizationFeatures(
        organizationId,
      );
      localStorage.setItem(
        'organizationFeatures',
        JSON.stringify(organizationFeatures),
      );
      localStorage.setItem(
        'organizationName',
        selectedOrganization?.name || '',
      );
      localStorage.setItem('currentOrganizationId', organizationId);

      window.open('/facility/facilitydashboard', '_blank');
    } catch (error) {
      console.error('Error logging into organization:', error);
      console.error('Full error object:', error.response || error);
    }
  };

  const handleLoginToFacilityPortal = async (organizationId) => {
    try {
      const selectedOrganization = organizations.find(
        (org) => org.id === organizationId,
      );

      posthog.capture('admin login to facility portal', {
        organizationId: organizationId,
        organizationName: selectedOrganization?.name,
      });

      const { handoffToken } = await OrganizationsApi.getFacilityPortalHandoff(
        organizationId,
      );

      if (!handoffToken) {
        console.error('Facility portal handoff token missing in response');
        return;
      }

      window.open(
        `${FACILITY_PORTAL_URL}/user/facilitylogin?handoff=${encodeURIComponent(
          handoffToken,
        )}&redirect=/payments`,
        '_blank',
        'noopener,noreferrer',
      );
    } catch (error) {
      console.error('Error opening facility portal:', error);
      console.error('Full error object:', error.response || error);
    }
  };

  return (
    <GridContainer>
      <GridItem xs={12} sm={12} md={12}>
        <Card>
          <CardHeader color='primary'>
            <h4>{t('organizations.title')}</h4>
            {errorMessage && <p style={{ color: '#ff0000' }}>{errorMessage}</p>}
          </CardHeader>

          <CardBody>
            <div>
              {isLoading && (
                <div className='loaders' style={CENTERED_LOADER_STYLE}>
                  <img src={loader} />
                </div>
              )}
              <div className='t-header'>
                <div className='t-header-left'>
                  <Link to={`/admin/createOrganization`} className='link'>
                    <span className='add-icon'>
                      <AddIcon />
                    </span>
                    {t('organizations.createButton')}
                  </Link>
                </div>
                <div className='t-header-right'>
                  <div className='custom-search-box'>
                    <input
                      type='search'
                      className='seacrh-field'
                      value={searchTerm}
                      maxLength={MAX_SEARCH_LENGTH}
                      onChange={handleSearchChange}
                      placeholder={t('organizations.searchPlaceholder')}
                      aria-label={t('organizations.searchPlaceholder')}
                    />
                    <span className='search-icon'>
                      <SearchIcon />
                    </span>
                  </div>
                </div>
              </div>

              <Table width='100%' id='template'>
                <TableHead>
                  <TableRow>
                    <TableCell align='left'>
                      {t('organizations.table.name')}
                    </TableCell>
                    <TableCell align='left'>
                      {t('organizations.table.createdBy')}
                    </TableCell>
                    <TableCell align='left'>
                      {t('organizations.table.createdAt')}
                    </TableCell>
                    <TableCell align='left'>
                      {t('organizations.table.customEmailDomain')}
                    </TableCell>
                    <TableCell align='left'>
                      {t('organizations.table.giftcardUids')}
                    </TableCell>
                    <TableCell align='left'>
                      {t('organizations.table.donationUids')}
                    </TableCell>
                    <TableCell align='left'>
                      {t('organizations.table.actions')}
                    </TableCell>
                  </TableRow>
                </TableHead>

                {organizations.length > 0 ? (
                  <TableBody>
                    {(rowsPerPage > 0
                      ? organizations.slice(
                          page * rowsPerPage,
                          page * rowsPerPage + rowsPerPage,
                        )
                      : organizations
                    ).map((obj) => (
                      <TableRow className='txtalign' key={obj.id}>
                        <TableCell width='20%'>
                          {obj && obj.name}
                          {obj?.managed_account_id != null && (
                            <Chip
                              size='small'
                              color='primary'
                              label={t('organizations.managedOrganization')}
                              sx={{ ml: 1 }}
                            />
                          )}
                        </TableCell>
                        <TableCell width='20%'>
                          {obj && obj.created_by}
                        </TableCell>
                        <TableCell width='10%'>
                          {obj && obj.created_at}
                        </TableCell>
                        <TableCell width='10%'>{obj && obj.domains}</TableCell>
                        <TableCell width='10%'>
                          {(obj && obj.tango_giftcards_uids?.length) || 0}
                        </TableCell>
                        <TableCell width='10%'>
                          {(obj && obj.tango_donations_uids?.length) || 0}
                        </TableCell>
                        <TableCell width='10%'>
                          {obj && obj.id && (
                            <>
                              <Link
                                to={`/admin/updateOrganization/${obj.id}`}
                                title={t('organizations.updateTitle')}>
                                <span className='material-icons'>edit</span>
                              </Link>
                              <button
                                onClick={() =>
                                  handleLoginToOrganization(obj.id)
                                }
                                title={t('organizations.loginTitle')}
                                style={{
                                  background: 'none',
                                  border: 'none',
                                  cursor: 'pointer',
                                  marginLeft: '8px',
                                }}>
                                <span
                                  className='material-icons'
                                  style={{ color: '#3AB795' }}>
                                  login
                                </span>
                              </button>
                              <button
                                type='button'
                                aria-label={t('organizations.loginPortalTitle')}
                                onClick={() =>
                                  handleLoginToFacilityPortal(obj.id)
                                }
                                title={t('organizations.loginPortalTitle')}
                                style={{
                                  background: 'none',
                                  border: 'none',
                                  cursor: 'pointer',
                                  marginLeft: '8px',
                                }}>
                                <span
                                  className='material-icons'
                                  style={{ color: '#1976D2' }}>
                                  open_in_new
                                </span>
                              </button>
                            </>
                          )}{' '}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                ) : (
                  !isLoading && (
                    <TableBody>
                      <TableRow>
                        <TableCell colSpan={7}>
                          {getEmptyTableMessage()}
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  )
                )}
                <TableFooter>
                  <TableRow align='center'>
                    <TablePagination
                      rowsPerPageOptions={[
                        5,
                        10,
                        25,
                        { label: t('common.labels.all'), value: -1 },
                      ]}
                      colSpan={7}
                      count={organizations.length}
                      rowsPerPage={rowsPerPage}
                      page={page}
                      SelectProps={{
                        inputProps: { roboto: 'rows per page' },
                        native: true,
                      }}
                      onPageChange={handleChangePage}
                      onRowsPerPageChange={handleRowsPerPage}
                      ActionsComponent={TablePaginationActions}
                    />
                  </TableRow>
                </TableFooter>
              </Table>
            </div>
          </CardBody>
        </Card>
      </GridItem>
    </GridContainer>
  );
}
